import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Check, X, Bell, Search, Calendar,
  ChevronDown, Home, FileText, MessageSquare, FileBarChart2, Settings,
  Inbox, BarChart3, Layers, Zap, ShieldCheck, Clock,
  TrendingUp, PoundSterling, GitBranch, Users, Briefcase, HelpCircle,
  Sparkles, Activity, Bot, Mail, Linkedin, Twitter, AtSign, FileCheck2,
  Building2, Scale, Wrench, AlertCircle,
} from "lucide-react";
import FeatureSequenceSection from "../components/landing/FeatureSequenceSection";
import FlowCentreSection from "../components/landing/FlowCentreSection";
import InsideLiveDeal from "../components/landing/InsideLiveDeal";
import { ChainFlowLogo } from "../components/brand/ChainFlowLogo";
import {
  IndustryProblemSection,
  CommandCentreSection,
  HowItWorksSection,
} from "../components/landing/MiddleSections";

/* ─────────────────────────────────────────────────────────
   LIVE-FEEL HOOKS — used by the hero Transaction Dashboard
   to make the surface feel like a real-time OS, not a mock.
   ───────────────────────────────────────────────────────── */
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

const useCountUp = (target, duration = 800) => {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    if (from === target) return undefined;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setValue(from + (target - from) * easeOut(t));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = target;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return value;
};

const useTicker = (initial, makeNext, intervalMs) => {
  const [value, setValue] = useState(initial);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setValue((prev) => makeNext(prev));
      setTick((t) => t + 1);
    }, intervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);
  return [value, tick];
};

const fmtGBPCompact = (n) => {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `£${Math.round(n / 1_000)}K`;
  return `£${Math.round(n).toLocaleString()}`;
};
const fmtGBPFull = (n) => `£${Math.round(n).toLocaleString()}`;

/* ─────────────────────────────────────────────────────────
   BRAND PALETTE — sampled from the ChainFlow logo
   #4F6BFF electric blue · #3B4BFB deep indigo · #4338CA royal purple
   Use these tokens everywhere — gradients, CTAs, glows, accents.
   ───────────────────────────────────────────────────────── */
const BRAND = {
  blue:   "#4F6BFF",
  indigo: "#3B4BFB",
  purple: "#4338CA",
  // Refined cobalt-indigo gradient — matches rest of platform
  gradient: "linear-gradient(135deg, #4F6BFF 0%, #3B4BFB 50%, #4338CA 100%)",
  // Soft elevated CTA shadow
  shadowMd: "0 16px 40px -14px rgba(67,56,202,0.45), 0 0 0 1px rgba(67,56,202,0.18) inset",
  shadowSm: "0 10px 26px -8px rgba(67,56,202,0.45), 0 0 0 1px rgba(67,56,202,0.18) inset",
  // Tints for atmospheric backdrops
  tintSoft:   "rgba(79,107,255,0.16)",
  tintLight:  "rgba(99,102,241,0.18)",
  tintDeep:   "rgba(67,56,202,0.20)",
};

/* ─────────────────────────────────────────────────────────
   BRAND — universal ChainFlowLogo (single source of truth)
   ───────────────────────────────────────────────────────── */
/* Legacy wrappers kept so other in-file call-sites compile,
   but they now render the canonical ChainFlowLogo. */
const BrandMark = ({ size = 28 }) => (
  <ChainFlowLogo variant="icon" size={size} />
);

const Wordmark = ({ size = 22 }) => (
  <ChainFlowLogo variant="wordmark" size={size} />
);

/* ─────────────────────────────────────────────────────────
   NAVBAR — thinner, scroll-aware blur + shadow
   ───────────────────────────────────────────────────────── */
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "cf-nav-scrolled" : "bg-white/40 backdrop-blur-md border-b border-transparent"}`}
      data-testid="landing-nav"
    >
      <div className={`max-w-[1280px] mx-auto px-8 lg:px-12 flex items-center justify-between gap-6 transition-all duration-300 ${scrolled ? "h-[64px]" : "h-[72px]"}`}>
        <Link to="/" className="flex items-center group shrink-0 py-1" data-testid="landing-logo">
          <ChainFlowLogo variant="full" size={28} />
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {["Product", "Solutions", "Resources", "Company", "Pricing"].map((item) => (
            <button
              key={item}
              type="button"
              className="text-[13px] font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 transition-colors"
              data-testid={`nav-${item.toLowerCase()}`}
            >
              {item}
              {item !== "Pricing" && <ChevronDown size={11} className="opacity-60" />}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <Link
            to="/login"
            className="hidden md:inline-flex h-9 px-3.5 rounded-lg text-slate-700 text-[13px] font-semibold items-center hover:text-slate-900 hover:bg-slate-100/70 transition-colors"
            data-testid="nav-secondary-cta"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            className="cf-cta-glow h-9 px-4 rounded-lg text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
            style={{
              background: BRAND.gradient,
              boxShadow: "0 10px 28px -10px rgba(67, 56, 202,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
            }}
            data-testid="nav-primary-cta"
          >
            Book a private demo <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    </nav>
  );
};

/* ─────────────────────────────────────────────────────────
   HERO — Transaction Dashboard mockup on the right (LIVE)
   ───────────────────────────────────────────────────────── */
const ACTIVITY_POOL = [
  { tag: "A", text: "Searches returned" },
  { tag: "M", text: "Memo issued" },
  { tag: "B", text: "Buyer uploaded ID" },
  { tag: "S", text: "Solicitor updated" },
  { tag: "M", text: "Mortgage offer received" },
  { tag: "C", text: "Contracts signed" },
  { tag: "E", text: "Enquiries raised" },
  { tag: "V", text: "Survey booked" },
  { tag: "P", text: "Proof of funds verified" },
  { tag: "X", text: "Exchange agreed" },
];

const fmtAgo = (msAgo) => {
  if (msAgo < 60_000) return "just now";
  const m = Math.floor(msAgo / 60_000);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
};

const HeroDashboardMockup = () => {
  /* ── Pipeline value: ±£20k every 4s with smooth count-up ── */
  const [pipelineTarget, pipelineTick] = useTicker(
    2_480_000,
    (prev) => {
      // Drift around the anchor so it feels stable but alive.
      const drift = (Math.random() * 40_000 - 20_000);
      const next = prev + drift;
      // Soft re-centre so it doesn't run away over time.
      return next + (2_480_000 - next) * 0.12;
    },
    4_000,
  );
  const pipelineDisplay = useCountUp(pipelineTarget, 900);

  /* ── Active deals: ±1 every 6s ── */
  const [activeDeals, activeTick] = useTicker(
    127,
    (prev) => Math.max(120, Math.min(135, prev + (Math.random() < 0.5 ? -1 : 1))),
    6_000,
  );

  /* ── Fee at risk: micro-tweak every 7s, biased to drift down (good news) ── */
  const [feeAtRiskTarget, feeTick] = useTicker(
    148_500,
    (prev) => {
      const drift = (Math.random() * 8_000 - 4_500);
      const next = Math.max(120_000, Math.min(170_000, prev + drift));
      return Math.round(next / 100) * 100;
    },
    7_000,
  );
  const feeAtRiskDisplay = useCountUp(feeAtRiskTarget, 900);

  /* ── Live activity feed: rotate top item every 5s ── */
  const baseActivities = useMemo(() => {
    const now = Date.now();
    return [
      { id: now - 1, tag: "A", text: "Searches returned",       at: now - 12 * 60_000 },
      { id: now - 2, tag: "M", text: "Memo issued",             at: now - 60 * 60_000 },
      { id: now - 3, tag: "B", text: "Buyer uploaded ID",       at: now - 2 * 3600_000 },
      { id: now - 4, tag: "S", text: "Solicitor updated",       at: now - 3 * 3600_000 },
      { id: now - 5, tag: "M", text: "Mortgage offer received", at: now - 5 * 3600_000 },
    ];
  }, []);
  const [activities, setActivities] = useState(baseActivities);
  useEffect(() => {
    const id = setInterval(() => {
      setActivities((prev) => {
        const pick = ACTIVITY_POOL[Math.floor(Math.random() * ACTIVITY_POOL.length)];
        const fresh = { id: Date.now() + Math.random(), tag: pick.tag, text: pick.text, at: Date.now() };
        return [fresh, ...prev.slice(0, 4)];
      });
    }, 5_000);
    return () => clearInterval(id);
  }, []);

  /* ── Needs chasing: highlight a different row briefly every ~6s ── */
  const chasingItems = useMemo(() => ([
    { title: "Solicitor inactive",          sub: "27 Tudor Street",  t: "4 days", tone: "rose" },
    { title: "Searches overdue",            sub: "26 Priory Lane",   t: "3 days", tone: "rose" },
    { title: "Mortgage valuation pending",  sub: "10 High Street",   t: "2 days", tone: "amber" },
    { title: "Management pack outstanding", sub: "7 Bolton Road",    t: "5 days", tone: "rose" },
  ]), []);
  const [highlightChase, setHighlightChase] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setHighlightChase(Math.floor(Math.random() * chasingItems.length));
    }, 6_000);
    return () => clearInterval(id);
  }, [chasingItems.length]);

  /* ─── Render ─── */
  return (
    <div
      className="relative rounded-[20px] md:rounded-t-none md:rounded-b-[20px] bg-white border border-slate-200/80 overflow-hidden"
      style={{ boxShadow: "0 60px 120px -30px rgba(15,23,42,0.34), 0 18px 40px -16px rgba(15,23,42,0.14)" }}
      data-testid="hero-dashboard-mock"
    >
      <div className="grid grid-cols-[180px_1fr]">
        {/* Sidebar */}
        <aside className="text-white px-4 py-5 space-y-1.5" style={{ background: "linear-gradient(180deg, #050B26 0%, #0E1454 100%)" }}>
          <div className="flex items-center gap-2 mb-4">
            <BrandMark size={24} white />
          </div>
          {[
            { Icon: Layers,         label: "Dashboard", active: true },
            { Icon: GitBranch,      label: "Deals" },
            { Icon: FileCheck2,     label: "Tasks", badge: "12" },
            { Icon: Calendar,       label: "Calendar" },
            { Icon: FileText,       label: "Documents" },
            { Icon: MessageSquare,  label: "Messages", badge: "5" },
            { Icon: FileBarChart2,  label: "Reports" },
            { Icon: Sparkles,       label: "Insights" },
            { Icon: Wrench,         label: "Integrations" },
            { Icon: Settings,       label: "Settings" },
          ].map(({ Icon, label, active, badge }) => (
            <div
              key={label}
              className={`flex items-center gap-2 px-2.5 h-8 rounded-lg text-[11.5px] ${active ? "bg-white/10 text-white" : "text-white/60 hover:text-white/85"}`}
            >
              <Icon size={11} />
              <span className="flex-1 truncate font-semibold">{label}</span>
              {badge && (
                <span className="text-[9.5px] font-bold text-white px-1.5 h-4 rounded-full inline-flex items-center" style={{ background: BRAND.indigo }}>{badge}</span>
              )}
            </div>
          ))}
          <div className="pt-3 mt-3 border-t border-white/10 flex items-center gap-2">
            <span className="w-7 h-7 rounded-full text-white text-[10px] font-bold flex items-center justify-center" style={{ background: BRAND.gradient }}>JC</span>
            <div className="leading-tight min-w-0">
              <p className="text-[11px] font-bold truncate">James Carter</p>
              <p className="text-[9.5px] text-white/55">Sales Manager</p>
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="px-5 py-4 bg-white">
          {/* Title row */}
          <div className="flex items-center justify-between mb-4">
            <p className="font-display text-[15px] font-bold text-slate-900 tracking-tight">Transaction Dashboard</p>
            <div className="flex items-center gap-2 text-[10.5px] text-slate-500">
              <span className="inline-flex items-center gap-1 px-2 h-6 rounded-md border border-slate-200">
                <Calendar size={10} /> Feb 1 – Feb 28, 2026 <ChevronDown size={9} />
              </span>
              <Search size={11} className="text-slate-400" />
              <span className="relative">
                <Bell size={11} className="text-slate-500" />
                <span className="absolute -top-0.5 -right-1 w-2.5 h-2.5 rounded-full bg-rose-500 cf-pulse" />
              </span>
              <span className="w-5 h-5 rounded-full bg-slate-300" />
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-4 gap-2.5 mb-4">
            {/* Pipeline value — LIVE */}
            <div
              key={`pipe-${pipelineTick}`}
              className="rounded-xl border border-slate-200 bg-white p-3 cf-cell-flash"
              data-testid="hero-kpi-pipeline"
            >
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500">Pipeline value</p>
              <p
                className="font-display text-[18px] font-bold tracking-tight mt-1 text-slate-900 cf-num tabular-nums"
                data-testid="hero-kpi-pipeline-value"
              >
                {fmtGBPCompact(pipelineDisplay)}
              </p>
              <p className="text-[9px] mt-0.5 text-emerald-600 font-semibold">▲ 27% vs last month</p>
            </div>

            {/* Active deals — LIVE */}
            <div
              key={`active-${activeTick}`}
              className="rounded-xl border border-slate-200 bg-white p-3 cf-cell-flash"
              data-testid="hero-kpi-active"
            >
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500">Active deals</p>
              <p className="font-display text-[18px] font-bold tracking-tight mt-1 text-slate-900 cf-num tabular-nums" data-testid="hero-kpi-active-value">
                {activeDeals}
              </p>
              <p className="text-[9px] mt-0.5 text-emerald-600 font-semibold">▲ 16% vs last month</p>
            </div>

            {/* Exchanges this month — static */}
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500">Exchanges this month</p>
              <p className="font-display text-[18px] font-bold tracking-tight mt-1 text-slate-900 cf-num tabular-nums">23</p>
              <p className="text-[9px] mt-0.5 text-emerald-600 font-semibold">▲ 35% vs last month</p>
            </div>

            {/* Fee at risk — LIVE (rose-tinted) */}
            <div
              key={`fee-${feeTick}`}
              className="rounded-xl border bg-rose-50/60 border-rose-200 p-3 cf-cell-flash"
              data-testid="hero-kpi-fee"
            >
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500">Fee at risk</p>
              <p className="font-display text-[18px] font-bold tracking-tight mt-1 text-rose-700 cf-num tabular-nums" data-testid="hero-kpi-fee-value">
                {fmtGBPFull(feeAtRiskDisplay)}
              </p>
              <p className="text-[9px] mt-0.5 leading-tight text-rose-600 whitespace-pre-line">4 delayed exchanges
2 inactive solicitors</p>
              <p className="text-[9px] mt-1 font-bold text-rose-700 inline-flex items-center gap-1">
                View at risk deals <ArrowRight size={9} />
              </p>
            </div>
          </div>

          {/* Three-column row: deal health donut · live activity · needs chasing */}
          <div className="grid grid-cols-3 gap-2.5 mb-4">
            {/* Donut */}
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5">Deal health</p>
              <div className="flex items-center gap-3">
                <div className="relative w-20 h-20">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r="38" fill="none" stroke="#E2E8F0" strokeWidth="14" />
                    <circle cx="50" cy="50" r="38" fill="none" stroke="#10B981" strokeWidth="14" strokeDasharray="98 240" strokeDashoffset="0" />
                    <circle cx="50" cy="50" r="38" fill="none" stroke="#F59E0B" strokeWidth="14" strokeDasharray="62 240" strokeDashoffset="-98" />
                    <circle cx="50" cy="50" r="38" fill="none" stroke="#FB7185" strokeWidth="14" strokeDasharray="44 240" strokeDashoffset="-160" />
                    <circle cx="50" cy="50" r="38" fill="none" stroke="#6366F1" strokeWidth="14" strokeDasharray="27 240" strokeDashoffset="-204" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
                    <p className="font-display text-[16px] font-bold text-slate-900">27</p>
                    <p className="text-[8px] text-slate-500">Total deals</p>
                  </div>
                </div>
                <ul className="space-y-1 text-[9.5px]">
                  <li className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> 11 Healthy</li>
                  <li className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> 7 Delayed</li>
                  <li className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-rose-400" /> 5 At risk</li>
                  <li className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500" /> 3 Critical</li>
                </ul>
              </div>
              <p className="text-[9px] font-semibold text-indigo-700 inline-flex items-center gap-0.5 mt-2">View all deals <ArrowRight size={8} /></p>
            </div>

            {/* Live activity — feed slides in new top item every 5s */}
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="hero-live-activity">
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5 inline-flex items-center gap-1">
                Live activity
                <span className="w-1 h-1 rounded-full bg-emerald-500 cf-pulse" />
              </p>
              <ul className="space-y-1.5">
                {activities.map((a, i) => (
                  <li
                    key={a.id}
                    className={`flex items-center gap-1.5 text-[10px] ${i === 0 ? "cf-feed-slide" : ""}`}
                  >
                    <span className="w-4 h-4 rounded-full bg-indigo-100 text-indigo-700 text-[8px] font-bold flex items-center justify-center">{a.tag}</span>
                    <span className="flex-1 truncate text-slate-800">{a.text}</span>
                    <span className="text-slate-400 cf-num">{fmtAgo(Date.now() - a.at)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[9px] font-semibold text-indigo-700 inline-flex items-center gap-0.5 mt-2">View all activity <ArrowRight size={8} /></p>
            </div>

            {/* Needs chasing — random row briefly highlights */}
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="hero-needs-chasing">
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5">Needs chasing</p>
              <ul className="space-y-1.5">
                {chasingItems.map((c, i) => (
                  <li
                    key={`${c.title}-${i === highlightChase ? "h" : "n"}`}
                    className={`flex items-start gap-1.5 text-[10px] rounded-md px-1 py-0.5 -mx-1 ${i === highlightChase ? "cf-row-flash" : ""}`}
                  >
                    <span className={`w-4 h-4 rounded-md flex items-center justify-center shrink-0 ${c.tone === "rose" ? "bg-rose-100 text-rose-600" : "bg-amber-100 text-amber-700"}`}>
                      <AlertCircle size={9} />
                    </span>
                    <div className="min-w-0 flex-1 leading-tight">
                      <p className="font-semibold text-slate-800 truncate">{c.title}</p>
                      <p className="text-[8.5px] text-slate-500 truncate">{c.sub}</p>
                    </div>
                    <span className={`cf-num text-[9px] font-bold ${c.tone === "rose" ? "text-rose-600" : "text-amber-700"}`}>{c.t}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[9px] font-semibold text-indigo-700 inline-flex items-center gap-0.5 mt-2">View all <ArrowRight size={8} /></p>
            </div>
          </div>

          {/* Bottom row of 4 secondary KPIs */}
          <div className="grid grid-cols-4 gap-2.5">
            {[
              { label: "Average time to exchange", value: "4.5 months", sub: "▼ 12% faster" },
              { label: "Fall-through rate",        value: "24%",        sub: "▼ 9% improvement" },
              { label: "Deals saved this month",   value: "6",          sub: "▲ 20%" },
              { label: "Average chase time",       value: "2.4 hrs",    sub: "▼ 16% faster" },
            ].map((k) => (
              <div key={k.label} className="rounded-xl border border-slate-200 bg-white p-2.5">
                <p className="text-[8.5px] font-semibold uppercase tracking-[0.12em] text-slate-500">{k.label}</p>
                <p className="font-display text-[15px] font-bold tracking-tight text-slate-900 mt-0.5">{k.value}</p>
                <p className="text-[9px] text-emerald-600 font-semibold mt-0.5">{k.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   FLOATING LIVE METRIC CARD — glassmorphism, animated counter
   ───────────────────────────────────────────────────────── */
const FloatingMetric = ({
  Icon,
  label,
  value,
  delta,
  deltaTone = "emerald",
  position,
  accent = BRAND.gradient,
  delay = 0,
  testid,
  pulse = false,
  float = "cf-float",
}) => (
  <div
    className={`absolute ${position} z-30 ${float} hidden md:block`}
    style={{ animationDelay: `${delay}ms` }}
    data-testid={testid}
  >
    <div
      className="relative rounded-2xl px-4 py-3.5 min-w-[210px]"
      style={{
        background: "rgba(255,255,255,0.72)",
        backdropFilter: "saturate(160%) blur(22px)",
        WebkitBackdropFilter: "saturate(160%) blur(22px)",
        border: "1px solid rgba(255,255,255,0.7)",
        boxShadow:
          "0 28px 70px -22px rgba(15,23,42,0.32), 0 8px 18px -10px rgba(79,107,255,0.18), inset 0 1px 0 rgba(255,255,255,0.7)",
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0 relative overflow-hidden"
          style={{
            background: accent,
            boxShadow: "0 10px 26px -10px rgba(67, 56, 202,0.5), inset 0 1px 0 rgba(255,255,255,0.32)",
          }}
        >
          <Icon size={16} strokeWidth={2.4} />
          {pulse && (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 flex h-2.5 w-2.5"
            >
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 cf-pulse" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 ring-2 ring-white" />
            </span>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            {label}
          </p>
          <p className="font-display text-[20px] font-bold tracking-tight text-slate-900 leading-none mt-1 tabular-nums">
            {value}
          </p>
        </div>
      </div>
      {delta && (
        <p
          className={`text-[11px] font-semibold mt-2 inline-flex items-center gap-1 ${
            deltaTone === "rose"
              ? "text-rose-600"
              : deltaTone === "amber"
              ? "text-amber-700"
              : "text-emerald-600"
          }`}
        >
          {delta}
        </p>
      )}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────
   HERO — premium Apple/Linear/Stripe inspired
   ───────────────────────────────────────────────────────── */
const HERO_LIVE_FEED = [
  "Memo issued · 27 Tudor Street",
  "Searches returned · 10 High Street",
  "Mortgage offer received · 26 Priory Lane",
  "Buyer uploaded ID · 7 Bolton Road",
  "Solicitor confirmed · 18 Heritage Mews",
  "Exchange agreed · 41 Station Road",
];

const Hero = () => {
  /* Live tickers for floating cards */
  const [pipelineTarget] = useTicker(
    2_480_000,
    (prev) => {
      const drift = Math.random() * 40_000 - 20_000;
      const next = prev + drift;
      return next + (2_480_000 - next) * 0.12;
    },
    4_000,
  );
  const pipelineDisplay = useCountUp(pipelineTarget, 900);

  const [activeDeals] = useTicker(
    127,
    (prev) => Math.max(120, Math.min(135, prev + (Math.random() < 0.5 ? -1 : 1))),
    6_000,
  );

  const [feeAtRiskTarget] = useTicker(
    148_500,
    (prev) => {
      const drift = Math.random() * 8_000 - 4_500;
      const next = Math.max(120_000, Math.min(170_000, prev + drift));
      return Math.round(next / 100) * 100;
    },
    7_000,
  );
  const feeAtRiskDisplay = useCountUp(feeAtRiskTarget, 900);

  const [activityIdx, setActivityIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setActivityIdx((i) => (i + 1) % HERO_LIVE_FEED.length),
      4_500,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative overflow-hidden" data-testid="landing-hero">
      {/* ── Atmospheric mesh background ── */}
      <div aria-hidden="true" className="absolute inset-0 -z-10 pointer-events-none">
        {/* Top-center violet glow */}
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1200px] h-[760px] rounded-full blur-3xl cf-glow-pulse"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(67, 56, 202,0.20) 0%, rgba(79,107,255,0.12) 35%, transparent 70%)",
          }}
        />
        {/* Left blue blob */}
        <div
          className="absolute top-40 -left-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-70"
          style={{
            background:
              "radial-gradient(circle, rgba(99,102,241,0.22) 0%, transparent 70%)",
          }}
        />
        {/* Right purple blob */}
        <div
          className="absolute top-72 -right-32 w-[640px] h-[640px] rounded-full blur-3xl opacity-70"
          style={{
            background:
              "radial-gradient(circle, rgba(67, 56, 202,0.18) 0%, transparent 70%)",
          }}
        />
        {/* Soft noise grid */}
        <div className="absolute inset-0 cf-hero-grid" />
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-20 lg:pb-28">
        {/* Eyebrow live pill */}
        <div className="flex justify-center mb-7 cf-fade-up">
          <div
            className="inline-flex items-center gap-2.5 h-9 px-4 rounded-full text-[12.5px] font-semibold text-slate-700"
            style={{
              background: "rgba(255,255,255,0.7)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              border: "1px solid rgba(79,107,255,0.18)",
              boxShadow: "0 6px 20px -10px rgba(79,107,255,0.30)",
            }}
            data-testid="hero-live-pill"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 cf-pulse" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-slate-600 font-medium">Live</span>
            <span className="w-px h-3 bg-slate-300/80" />
            <span className="tabular-nums font-bold text-slate-900">{activeDeals}</span>
            <span className="text-slate-600">deals progressing right now</span>
          </div>
        </div>

        {/* Premium positioning eyebrow */}
        <div
          className="text-center mb-5 cf-fade-up"
          style={{ animationDelay: "60ms" }}
          data-testid="hero-eyebrow"
        >
          <p
            className="inline-block text-[11px] font-semibold uppercase tracking-[0.32em]"
            style={{
              color: "rgba(67,56,202,0.75)",
              fontFeatureSettings: '"ss01"',
            }}
          >
            The Operating System for Property Transactions
          </p>
        </div>

        {/* Massive headline */}
        <h1
          className="font-display text-center font-bold tracking-[-0.045em] text-slate-900 leading-[0.95] cf-fade-up"
          style={{
            animationDelay: "120ms",
            fontSize: "clamp(48px, 9vw, 116px)",
          }}
          data-testid="hero-headline"
        >
          Every transaction.
          <br />
          Fully{" "}
          <span className="relative inline-block">
            <span
              style={{
                background: BRAND.gradient,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                filter: "drop-shadow(0 4px 28px rgba(67, 56, 202,0.30))",
              }}
            >
              connected.
            </span>
          </span>
        </h1>

        {/* Subheadline */}
        <p
          className="mt-7 text-center font-medium text-slate-600 max-w-[760px] mx-auto leading-relaxed cf-fade-up"
          style={{ animationDelay: "200ms", fontSize: "clamp(16px, 1.5vw, 21px)" }}
        >
          Run deals, communication, progression, and documents from one connected
          operating system — built for the way property transactions{" "}
          <em className="not-italic font-semibold text-slate-800">actually</em> work.
        </p>

        {/* CTAs */}
        <div
          className="mt-10 flex flex-wrap items-center justify-center gap-3 cf-fade-up"
          style={{ animationDelay: "280ms" }}
        >
          <Link
            to="/signup"
            className="cf-cta-glow group h-13 px-7 rounded-2xl text-white text-[15px] font-bold inline-flex items-center gap-2.5 transition-all hover:-translate-y-0.5"
            style={{
              background: BRAND.gradient,
              boxShadow:
                "0 22px 50px -16px rgba(67, 56, 202,0.5), 0 6px 14px -6px rgba(79,107,255,0.30), inset 0 1px 0 rgba(255,255,255,0.28)",
              height: 52,
            }}
            data-testid="hero-primary-cta"
          >
            See ChainFlow in action
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </Link>
          <Link
            to="/login"
            className="group h-13 px-6 rounded-2xl text-slate-900 text-[15px] font-bold inline-flex items-center gap-2 transition-all hover:-translate-y-0.5"
            style={{
              height: 52,
              background: "rgba(255,255,255,0.78)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              border: "1px solid rgba(15,23,42,0.08)",
              boxShadow: "0 8px 24px -12px rgba(15,23,42,0.10)",
            }}
            data-testid="hero-secondary-cta"
          >
            Explore the platform
            <ArrowUpRight
              size={15}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>

        {/* Trust line */}
        <div
          className="mt-9 flex flex-wrap items-center justify-center gap-2 cf-fade-up"
          style={{ animationDelay: "360ms" }}
          data-testid="hero-trust"
        >
          {[
            "Built for UK estate agencies",
            "Reduce deal fall-throughs",
            "Real-time chain visibility",
            "Multi-party collaboration",
            "Faster sales progression",
            "Replace email + WhatsApp chaos",
            "Bank-level security",
            "UK-hosted infrastructure",
            "Designed around real workflows",
            "Built from 14+ years in property",
          ].map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[11.5px] font-semibold text-slate-700 transition-all hover:-translate-y-0.5"
              style={{
                background: "rgba(255,255,255,0.72)",
                backdropFilter: "saturate(160%) blur(14px)",
                WebkitBackdropFilter: "saturate(160%) blur(14px)",
                border: "1px solid rgba(99,102,241,0.16)",
                boxShadow: "0 1px 0 rgba(255,255,255,0.7) inset, 0 6px 18px -10px rgba(67,56,202,0.16)",
              }}
            >
              <Check size={11} className="text-emerald-500" strokeWidth={3} /> {t}
            </span>
          ))}
        </div>

        {/* Premium dashboard composition with floating cards */}
        <div
          className="relative mt-16 lg:mt-20 cf-fade-up"
          style={{ animationDelay: "440ms" }}
        >
          {/* Glow under the dashboard */}
          <div aria-hidden="true" className="absolute -inset-x-12 -top-8 -bottom-16 -z-10 pointer-events-none">
            <div
              className="absolute inset-x-12 inset-y-0 rounded-[48px] blur-3xl opacity-80"
              style={{
                background:
                  "radial-gradient(ellipse at top, rgba(67, 56, 202,0.22) 0%, rgba(79,107,255,0.14) 35%, transparent 70%)",
              }}
            />
          </div>

          {/* Reflection underneath */}
          <div
            aria-hidden="true"
            className="absolute left-1/2 -translate-x-1/2 top-full w-[88%] h-32 -mt-2 pointer-events-none opacity-50 hidden lg:block"
            style={{
              background:
                "radial-gradient(ellipse at top, rgba(15,23,42,0.18), transparent 70%)",
              filter: "blur(20px)",
            }}
          />

          {/* Floating metric cards over the dashboard */}
          {/* Top-left: Live Deal Activity */}
          <FloatingMetric
            Icon={Activity}
            label="Live Deal Activity"
            value={
              <span className="block text-[12.5px] font-bold text-slate-900 truncate max-w-[180px]">
                {HERO_LIVE_FEED[activityIdx]}
              </span>
            }
            delta={<span><span className="cf-pulse inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1" />Updated just now</span>}
            position="-top-8 -left-4 lg:-left-10"
            delay={0}
            pulse
            float="cf-float"
            testid="hero-float-activity"
          />

          {/* Top-right: Deals progressing right now */}
          <FloatingMetric
            Icon={GitBranch}
            label="Deals progressing now"
            value={<>{activeDeals}</>}
            delta="▲ 16% vs last month"
            position="-top-8 -right-4 lg:-right-10"
            delay={400}
            float="cf-float-alt"
            testid="hero-float-active"
          />

          {/* Bottom-left: Pipeline this month */}
          <FloatingMetric
            Icon={TrendingUp}
            label="Pipeline this month"
            value={fmtGBPCompact(pipelineDisplay)}
            delta="▲ 27% vs last month"
            position="-bottom-8 -left-4 lg:-left-10"
            delay={800}
            float="cf-float-alt"
            testid="hero-float-pipeline"
          />

          {/* Bottom-right: Fees currently at risk */}
          <FloatingMetric
            Icon={AlertCircle}
            label="Fees at risk"
            value={fmtGBPFull(feeAtRiskDisplay)}
            delta="4 delayed exchanges"
            deltaTone="rose"
            position="-bottom-8 -right-4 lg:-right-10"
            accent="linear-gradient(135deg, #FB7185 0%, #E11D48 100%)"
            delay={1200}
            float="cf-float"
            testid="hero-float-fee"
          />

          {/* Dashboard mock with premium chrome */}
          <div className="relative">
            {/* Subtle browser-style chrome bar above */}
            <div
              className="hidden md:flex items-center gap-2 h-9 px-4 rounded-t-[20px] -mb-px relative z-10"
              style={{
                background:
                  "linear-gradient(180deg, rgba(248,250,252,0.95) 0%, rgba(255,255,255,0.95) 100%)",
                border: "1px solid rgba(226,232,240,0.9)",
                borderBottom: "1px solid rgba(226,232,240,0.6)",
                boxShadow: "0 -1px 0 rgba(255,255,255,0.6) inset",
              }}
            >
              <span className="w-2.5 h-2.5 rounded-full bg-rose-300/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-300/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-300/80" />
              <span className="ml-3 text-[10.5px] font-semibold text-slate-500 tracking-wide">
                app.chainflowos.com / dashboard
              </span>
              <span className="ml-auto text-[10.5px] font-semibold text-slate-400 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 cf-pulse" /> Live
              </span>
            </div>

            <HeroDashboardMockup />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────
   HERO STATS ROW — 4 metrics
   ───────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────
   HERO STATS ROW — premium, count-up on viewport entry
   ───────────────────────────────────────────────────────── */
const useInViewOnce = (rootMargin = "-15% 0px") => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin, threshold: 0.1 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);
  return [ref, inView];
};

const StatCountUp = ({ to, prefix = "", suffix = "", decimals = 0, inView, duration = 1400 }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setV(to * easeOut(t));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [to, inView, duration]);
  const formatted =
    decimals > 0
      ? v.toFixed(decimals)
      : Math.round(v).toLocaleString();
  return (
    <span className="tabular-nums">
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

const HeroStatsRow = () => {
  const [ref, inView] = useInViewOnce();
  const stats = [
    { Icon: Activity,      to: 42,      decimals: 0, prefix: "",  suffix: "",   unit: "",       label: "Active deals",          sub: "Live across all branches",     pulse: true },
    { Icon: PoundSterling, to: 184,     decimals: 0, prefix: "£", suffix: "k",  unit: "",       label: "Pipeline tracked",      sub: "Currently in motion",           pulse: false },
    { Icon: AlertCircle,   to: 3,       decimals: 0, prefix: "",  suffix: "",   unit: "",       label: "Risks detected",         sub: "Awaiting agent action",         pulse: true, tone: "rose" },
    { Icon: ShieldCheck,   to: 91,      decimals: 0, prefix: "",  suffix: "%",  unit: "",       label: "Response rate",          sub: "Solicitors · brokers · buyers", pulse: false, tone: "emerald" },
    { Icon: TrendingUp,    to: 14,      decimals: 0, prefix: "",  suffix: "",   unit: "days",   label: "Faster progression",     sub: "Avg vs UK industry",            pulse: false },
  ];
  return (
    <section
      ref={ref}
      className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-20 lg:pb-28"
      data-testid="hero-stats-row"
    >
      {/* Section eyebrow */}
      <div className="text-center mb-7">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.32em]" style={{ color: "rgba(67,56,202,0.70)" }}>
          Live Flow Centre
        </p>
      </div>

      <div className="relative grid grid-cols-2 md:grid-cols-5 gap-3 lg:gap-4">
        {/* Atmospheric backdrop */}
        <span
          aria-hidden="true"
          className="absolute -inset-x-8 -inset-y-6 -z-10 rounded-[36px] blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(99,102,241,0.10) 0%, rgba(79,107,255,0.06) 40%, transparent 75%)",
          }}
        />

        {stats.map((s, i) => {
          const tone = s.tone || "indigo";
          const accentColor =
            tone === "rose"    ? "#E11D48"
          : tone === "emerald" ? "#059669"
          :                      "#4F6BFF";
          const accentTint =
            tone === "rose"    ? "rgba(244,63,94,0.10)"
          : tone === "emerald" ? "rgba(16,185,129,0.10)"
          :                      "rgba(99,102,241,0.10)";
          return (
            <div
              key={i}
              className="relative rounded-2xl px-5 py-5 transition-transform hover:-translate-y-0.5 cf-fade-up"
              style={{
                background: "rgba(255,255,255,0.78)",
                backdropFilter: "saturate(160%) blur(18px)",
                WebkitBackdropFilter: "saturate(160%) blur(18px)",
                border: "1px solid rgba(99,102,241,0.14)",
                boxShadow:
                  "0 1px 0 rgba(255,255,255,0.7) inset, 0 18px 40px -22px rgba(15,23,42,0.14), 0 4px 14px -8px rgba(67,56,202,0.06)",
                animationDelay: `${100 + i * 80}ms`,
              }}
            >
              {/* Top icon + live dot */}
              <div className="flex items-start justify-between mb-3">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{
                    background: accentTint,
                    border: `1px solid ${accentTint.replace("0.10", "0.24")}`,
                    color: accentColor,
                  }}
                >
                  <s.Icon size={15} strokeWidth={2.2} />
                </div>
                {s.pulse && (
                  <span className="relative flex h-2 w-2 mt-1.5">
                    <span
                      className="absolute inline-flex h-full w-full rounded-full opacity-70 cf-pulse"
                      style={{ background: tone === "rose" ? "#FB7185" : "#34D399" }}
                    />
                    <span
                      className="relative inline-flex rounded-full h-2 w-2"
                      style={{ background: tone === "rose" ? "#E11D48" : "#10B981" }}
                    />
                  </span>
                )}
              </div>
              {/* Number */}
              <p className="font-display font-bold tracking-tight text-slate-900 tabular-nums leading-none"
                style={{ fontSize: "clamp(24px, 3vw, 32px)" }}>
                <StatCountUp
                  inView={inView}
                  to={s.to}
                  prefix={s.prefix}
                  suffix={s.suffix}
                  decimals={s.decimals}
                />
                {s.unit && (
                  <span className="ml-1 text-[13px] font-semibold text-slate-500">
                    {s.unit}
                  </span>
                )}
              </p>
              {/* Label + sub */}
              <p className="text-[11.5px] font-semibold text-slate-700 mt-2 leading-tight">{s.label}</p>
              <p className="text-[10.5px] text-slate-500 mt-0.5 leading-tight">{s.sub}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────
   OLD WAY VS CHAINFLOW WAY
   ───────────────────────────────────────────────────────── */
const OldWayVisual = () => (
  <div className="relative h-[260px] lg:h-[320px] w-full">
    {/* Center house */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-2xl bg-indigo-50 border border-indigo-200 flex items-center justify-center">
      <Home size={26} className="text-indigo-700" />
    </div>
    {/* Scattered messy icons */}
    <div className="absolute left-[12%] top-[8%] w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
      <Mail size={18} className="text-rose-500" />
    </div>
    <div className="absolute right-[15%] top-[5%] w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
      <span className="relative">
        <FileText size={18} className="text-slate-700" />
        <span className="absolute -top-2 -right-3 text-[9px] font-bold bg-rose-500 text-white rounded-full px-1.5 h-4 inline-flex items-center">23</span>
      </span>
    </div>
    <div className="absolute left-[5%] top-[40%] w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
      <FileText size={18} className="text-slate-700" />
    </div>
    <div className="absolute right-[6%] top-[42%] w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
      <FileText size={18} className="text-slate-700" />
    </div>
    <div className="absolute left-[15%] bottom-[7%] w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
      <FileBarChart2 size={18} className="text-emerald-700" />
    </div>
    <div className="absolute left-[42%] bottom-[3%] w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
      <FileText size={18} className="text-slate-700" />
    </div>
    <div className="absolute right-[15%] bottom-[5%] w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
      <span className="relative">
        <MessageSquare size={18} className="text-emerald-600" />
        <span className="absolute -top-2 -right-3 text-[9px] font-bold bg-rose-500 text-white rounded-full px-1.5 h-4 inline-flex items-center">47</span>
      </span>
    </div>
    {/* Dotted lines */}
    <svg viewBox="0 0 600 320" className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
      {[
        "M85 65 Q200 130 300 160", "M520 50 Q400 100 300 160",
        "M55 165 Q160 165 290 160", "M540 175 Q420 170 310 160",
        "M105 285 Q200 240 300 165", "M295 310 Q300 230 300 165",
        "M515 290 Q400 230 305 165",
      ].map((d, i) => (
        <path key={i} d={d} fill="none" stroke="#FB7185" strokeWidth="1.4" strokeDasharray="3 4" opacity="0.55" />
      ))}
    </svg>
  </div>
);

const ChainFlowWayVisual = () => (
  <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)] gap-3">
    {/* Progression timeline card */}
    <div className="rounded-2xl bg-white border border-slate-200 p-4">
      <div className="flex items-start gap-2 mb-3">
        <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0">
          <GitBranch size={14} />
        </span>
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500">Deal Room</p>
          <p className="text-[12px] font-bold text-slate-900">27 The Avenue</p>
          <p className="text-[10.5px] text-slate-500">Sale agreed</p>
        </div>
      </div>
      <ol className="relative pl-5 space-y-2.5">
        <span aria-hidden="true" className="absolute left-[7px] top-1 bottom-1 w-px bg-emerald-200" />
        {[
          { state: "done", label: "Offer accepted",       date: "Jan 12" },
          { state: "done", label: "AML checks complete",  date: "Jan 14" },
          { state: "done", label: "Searches returned",    date: "Jan 21" },
          { state: "done", label: "Enquiries raised",     date: "Jan 22" },
          { state: "current", label: "Exchange",          date: "In progress" },
          { state: "upcoming", label: "Completion",       date: "Upcoming" },
        ].map((s, i) => (
          <li key={i} className="flex items-center gap-3">
            <span className={`absolute -left-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center ${
              s.state === "done" ? "bg-emerald-500 text-white"
              : s.state === "current" ? "bg-indigo-500 text-white"
              : "bg-white border border-slate-300"
            }`}>
              {s.state === "done" && <Check size={8} strokeWidth={3} />}
              {s.state === "current" && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
            </span>
            <p className={`text-[11.5px] flex-1 ${s.state === "upcoming" ? "text-slate-400" : "font-semibold text-slate-800"}`}>{s.label}</p>
            <p className="text-[10.5px] text-slate-400 cf-num">{s.date}</p>
          </li>
        ))}
      </ol>
    </div>

    {/* Side activity cards */}
    <div className="space-y-2.5">
      {[
        { Icon: Scale,    title: "Solicitor", body: "Searches have been returned.", t: "12m ago", tone: "violet" },
        { Icon: FileText, title: "Document",  body: "Official Search · Local Authority", t: "1h ago", tone: "indigo" },
        { Icon: Users,    title: "Buyer",     body: "ID documents uploaded", t: "2h ago", tone: "violet" },
      ].map((c, i) => (
        <div key={i} className="rounded-xl bg-white border border-slate-200 p-3">
          <div className="flex items-start gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0">
              <c.Icon size={14} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500">{c.title}</p>
              <p className="text-[11.5px] font-semibold text-slate-800 mt-0.5 leading-tight truncate">{c.body}</p>
            </div>
            <p className="text-[9.5px] text-slate-400 cf-num shrink-0">{c.t}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const OldVsNewSection = () => (
  <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-24 lg:pb-32" data-testid="landing-old-vs-new">
    {/* Section opener */}
    <div className="text-center mb-12 lg:mb-16">
      <p
        className="text-[11px] font-bold uppercase tracking-[0.24em] cf-fade-up"
        style={{
          background: BRAND.gradient,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        The Status Quo
      </p>
      <h2
        className="font-display font-bold tracking-[-0.035em] text-slate-900 mt-3 leading-[1.04] cf-fade-up"
        style={{ fontSize: "clamp(32px, 4.6vw, 56px)", animationDelay: "100ms" }}
      >
        The way property is sold today
        <br />
        is fundamentally broken.
      </h2>
      <p
        className="mt-5 text-[15.5px] lg:text-[17px] text-slate-600 max-w-[640px] mx-auto leading-relaxed cf-fade-up"
        style={{ animationDelay: "180ms" }}
      >
        £148M in agency fees fall through every year because chains are managed
        across email, spreadsheets, and group chats. ChainFlow replaces all three.
      </p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
      {/* OLD WAY */}
      <div
        className="relative rounded-3xl p-7 lg:p-9 transition-transform hover:-translate-y-0.5"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,241,242,0.45) 0%, rgba(255,255,255,0.95) 100%)",
          border: "1px solid rgba(244,63,94,0.18)",
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.7) inset, 0 24px 50px -28px rgba(15,23,42,0.18)",
        }}
      >
        <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-rose-600">The old way</p>
        <h3 className="font-display text-[26px] lg:text-[30px] font-bold text-slate-900 tracking-[-0.02em] mt-2 leading-tight">
          Property transactions are still managed like it's 2008.
        </h3>
        <p className="text-[13px] text-slate-600 mt-3 max-w-[520px] leading-relaxed">
          The average UK property transaction still takes 4.5 months to exchange.
        </p>
        <ul className="mt-6 space-y-3">
          {[
            "Endless emails and WhatsApp chains",
            "Spreadsheets and manual trackers",
            "Documents lost or out of date",
            "Chasing for updates every day",
            "No real-time visibility or accountability",
          ].map((p) => (
            <li key={p} className="flex items-center gap-3 text-[13.5px] text-slate-700">
              <span className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                <X size={11} strokeWidth={3} />
              </span>
              {p}
            </li>
          ))}
        </ul>
        <div className="mt-7">
          <OldWayVisual />
        </div>
      </div>

      {/* CHAINFLOW WAY */}
      <div
        className="relative rounded-3xl p-7 lg:p-9 transition-transform hover:-translate-y-0.5 overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, rgba(236,253,245,0.35) 0%, rgba(255,255,255,0.95) 100%)",
          border: "1px solid rgba(16,185,129,0.20)",
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.7) inset, 0 24px 50px -28px rgba(15,23,42,0.18), 0 0 0 1px rgba(16,185,129,0.04)",
        }}
      >
        {/* Subtle live indicator */}
        <span
          aria-hidden="true"
          className="absolute top-5 right-5 inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-emerald-50 border border-emerald-200"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 cf-pulse" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
          </span>
          <span className="text-[9.5px] font-bold text-emerald-700 uppercase tracking-[0.12em]">Live</span>
        </span>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-emerald-700">The ChainFlow way</p>
        <h3 className="font-display text-[26px] lg:text-[30px] font-bold text-slate-900 tracking-[-0.02em] mt-2 leading-tight">
          Connected. Clear.<br />In control.
        </h3>
        <ul className="mt-6 space-y-3">
          {[
            "Connected Deal Rooms for every transaction",
            "Real-time timelines and intelligent alerts",
            "Centralised communication and updates",
            "One secure source of truth",
            "Everyone aligned and accountable",
            "Faster progression. Fewer fall-throughs.",
          ].map((p) => (
            <li key={p} className="flex items-center gap-3 text-[13.5px] text-slate-700">
              <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                <Check size={11} strokeWidth={3} />
              </span>
              {p}
            </li>
          ))}
        </ul>
        <div className="mt-7">
          <ChainFlowWayVisual />
        </div>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────
   FINAL CTA
   ───────────────────────────────────────────────────────── */
const FinalCTASection = () => (
  <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-24 lg:pb-32 pt-8" data-testid="landing-final-cta">
    <div
      className="relative rounded-[28px] text-white px-7 lg:px-14 py-12 lg:py-16 overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #050B26 0%, #0E1768 50%, #1A0E5C 100%)",
        boxShadow:
          "0 60px 120px -30px rgba(15,23,42,0.50), 0 8px 24px -8px rgba(79,107,255,0.30), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
    >
      {/* Cinematic flares */}
      <span aria-hidden="true" className="absolute -bottom-24 -right-12 w-[480px] h-[280px] rounded-full blur-3xl"
        style={{ background: "radial-gradient(ellipse at center, rgba(79,107,255,0.50), rgba(15,23,42,0) 65%)" }} />
      <span aria-hidden="true" className="absolute -bottom-32 -right-32 w-[260px] h-[260px] rounded-full blur-2xl"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.32), rgba(15,23,42,0) 70%)" }} />
      <span aria-hidden="true" className="absolute -top-20 -left-20 w-[360px] h-[360px] rounded-full blur-3xl cf-glow-pulse"
        style={{ background: "radial-gradient(circle, rgba(67, 56, 202,0.34), rgba(15,23,42,0) 72%)" }} />
      {/* Soft grid */}
      <span
        aria-hidden="true"
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(ellipse 70% 60% at 30% 50%, black 30%, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 60% at 30% 50%, black 30%, transparent 75%)",
        }}
      />

      <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,0.62fr)_minmax(0,0.38fr)] gap-10 items-center">
        <div>
          {/* Live pill */}
          <div
            className="inline-flex items-center gap-2 h-8 px-3 rounded-full mb-6"
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.12)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 cf-pulse" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-[11px] font-semibold text-white/80 tracking-wide">
              Onboarding new agencies this week
            </span>
          </div>
          <h2
            className="font-display font-bold tracking-[-0.035em] leading-[1.02]"
            style={{ fontSize: "clamp(34px, 5vw, 60px)" }}
          >
            Join the future of
            <br />
            <span
              style={{
                background:
                  "linear-gradient(135deg, #93C5FD 0%, #818CF8 50%, #A5B4FC 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              property transactions.
            </span>
          </h2>
          <p className="text-[14.5px] lg:text-[16px] text-white/70 mt-5 max-w-[520px] leading-relaxed">
            One platform. Every stakeholder. Faster outcomes. See ChainFlow run a
            live deal in 20 minutes.
          </p>
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12px]">
            {[
              { Icon: ShieldCheck, title: "Personalised demo",       sub: "See ChainFlow in action" },
              { Icon: FileCheck2,  title: "No commitment",            sub: "Just value, upfront" },
              { Icon: Building2,   title: "Built for UK agencies",    sub: "Local expertise, real results" },
            ].map((t) => (
              <div key={t.title} className="flex items-start gap-2.5">
                <span
                  className="w-8 h-8 rounded-lg text-white flex items-center justify-center shrink-0"
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.10)",
                  }}
                >
                  <t.Icon size={13} />
                </span>
                <div className="leading-tight pt-0.5">
                  <p className="text-[12px] font-bold">{t.title}</p>
                  <p className="text-[10.5px] text-white/60 mt-0.5">{t.sub}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/signup"
              className="cf-cta-glow group h-12 px-6 rounded-xl text-white text-[14px] font-bold inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
              style={{
                background: BRAND.gradient,
                boxShadow:
                  "0 22px 50px -16px rgba(67, 56, 202,0.7), 0 6px 14px -6px rgba(79,107,255,0.40), inset 0 1px 0 rgba(255,255,255,0.28)",
              }}
              data-testid="landing-final-cta-button"
            >
              Book your private demo
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/pricing"
              className="group h-12 px-5 rounded-xl text-[14px] font-bold inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.14)",
                color: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
              }}
            >
              View pricing
              <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────
   FOOTER
   ───────────────────────────────────────────────────────── */
const Footer = () => (
  <footer className="border-t border-slate-200 bg-white" data-testid="landing-footer">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
      {/* Brand */}
      <div className="col-span-2 md:col-span-1">
        <Link to="/" className="inline-flex items-center">
          <ChainFlowLogo variant="full" size={30} />
        </Link>
        <p className="text-[12px] text-slate-500 mt-3 leading-relaxed max-w-[230px]">
          Transaction infrastructure for modern estate agencies.
        </p>
        <div className="flex items-center gap-2 mt-4">
          {[Linkedin, Twitter, AtSign].map((Icon, i) => (
            <a key={i} href="#" className="w-9 h-9 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-600 inline-flex items-center justify-center transition-colors">
              <Icon size={13} />
            </a>
          ))}
        </div>
      </div>

      {/* Platform */}
      <div>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Platform</p>
        <ul className="space-y-2 text-[12.5px]">
          {["Deal Rooms", "Sales Progression", "Risk Scoring", "Document Hub"].map((l) => (
            <li key={l}><a href="#" className="text-slate-700 hover:text-slate-900 transition-colors">{l}</a></li>
          ))}
        </ul>
      </div>

      {/* Company */}
      <div>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Company</p>
        <ul className="space-y-2 text-[12.5px]">
          {["About", "Founding Story", "Pricing", "Contact"].map((l) => (
            <li key={l}><a href="#" className="text-slate-700 hover:text-slate-900 transition-colors">{l}</a></li>
          ))}
        </ul>
      </div>

      {/* Legal */}
      <div>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Legal</p>
        <ul className="space-y-2 text-[12.5px]">
          {["Privacy", "Terms", "Security", "Compliance"].map((l) => (
            <li key={l}><a href="#" className="text-slate-700 hover:text-slate-900 transition-colors">{l}</a></li>
          ))}
        </ul>
      </div>
    </div>
    <div className="border-t border-slate-100">
      <p className="max-w-[1280px] mx-auto px-6 lg:px-10 py-5 text-[11px] text-slate-500 text-center">
        © 2026 ChainFlow OS Ltd · Built for the next generation of property transactions.
      </p>
    </div>
  </footer>
);

/* ─────────────────────────────────────────────────────────
   PUBLIC NAMED EXPORTS
   Used by other public pages (e.g. PricingPage) that want
   to share the same nav, footer, and a tiny scroll reveal.
   ───────────────────────────────────────────────────────── */
export const TopNav = Nav;
export { Footer };

export const Reveal = ({ children, delay = 0 }) => (
  <div className="cf-reveal" style={delay ? { animationDelay: `${delay}ms` } : undefined}>
    {children}
  </div>
);

/* ─────────────────────────────────────────────────────────
   PAGE
   ───────────────────────────────────────────────────────── */
export default function LandingPage() {
  return (
    <div
      className="min-h-screen overflow-x-clip"
      style={{
        background: "linear-gradient(180deg, #FCFBFF 0%, #F7F4FE 30%, #FBFAFE 100%)",
      }}
      data-testid="landing-page"
    >
      <Nav />
      <Hero />
      <InsideLiveDeal />
      <FlowCentreSection />
      <IndustryProblemSection />
      <CommandCentreSection />
      <FeatureSequenceSection />
      <HowItWorksSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
}
